/// <reference types="vite/client" />
import { createInertiaApp, router } from '@inertiajs/react'
import { createRoot } from 'react-dom/client'
import '../css/application.css'
import { initCatalog } from '@/lib/i18n'
import { primeAudioOnFirstGesture } from '@/lib/sound'

declare global {
  interface Window {
    // views/shared/_google_analytics.html.erb 里的内联片段声明的全局函数。
    // 那段只在生产渲染，所以其余环境下这里是 undefined。
    gtag?: (...args: unknown[]) => void
  }
}

// 必须在 createInertiaApp 之前：页面组件在渲染时就会调 t()。
initCatalog()

// 在第一次任意手势（点一下、按一个键）里把 AudioContext 建起来。打卡音是网络
// 响应回来之后才播的，那时早就脱离了手势上下文，Chrome 的自动播放策略会拒绝
// 后补的 resume()——所以必须现在就订阅，不能等到真正要放声音那一刻。
primeAudioOnFirstGesture()

createInertiaApp({
  resolve: (name) => {
    const pages = import.meta.glob('../pages/**/*.tsx', { eager: true }) as Record<
      string,
      { default: React.ComponentType }
    >
    return pages[`../pages/${name}.tsx`]
  },
  setup({ el, App, props }) {
    createRoot(el).render(<App {...props} />)
  },
})

// Inertia 是客户端换页：gtag 片段自带的那条 page_view 只在首屏打一次，之后
// router 换页 GA 完全不知道，报表里会只剩一个入口页。这里给每次导航补一条。
let lastTrackedUrl = window.location.pathname + window.location.search

// navigate 事件比标题更新早：页面标题是 Inertia 的 <Head> 在 React 渲染提交后才写进
// document 的，而 React 18 的调度让这次提交可能落在好几个宏任务之后——实测在
// setTimeout(0) 里读到的仍是**上一页**的标题，直接上报会让每个页面都套上前一页的标题。
// 所以等 <head> 里的 title 真的变了再上报。
//
// 不用 requestAnimationFrame 做延迟：它在页面不可见时（后台标签页、窗口最小化）根本
// 不执行，那次导航会被静默丢掉；定时器在后台只是被节流，仍然会跑。
//
// 观察整个 <head> 而不是 <title> 元素本身：<Head> 有可能是替换掉整个 title 节点，
// 那样盯着旧节点收不到通知。
function whenTitleUpdated(previousTitle: string, timeoutMs = 1000): Promise<void> {
  if (document.title !== previousTitle) return Promise.resolve()

  return new Promise((resolve) => {
    const finish = () => {
      observer.disconnect()
      clearTimeout(timer)
      resolve()
    }
    const observer = new MutationObserver(() => {
      if (document.title !== previousTitle) finish()
    })
    observer.observe(document.head, { childList: true, subtree: true, characterData: true })
    // 兜底：有些页面没写 <Head>，标题不会变，不能就此不上报。
    const timer = setTimeout(finish, timeoutMs)
  })
}

router.on('navigate', (event) => {
  const url = event.detail.page.url
  // navigate 在首屏也会触发，那次会和片段自带的 page_view 撞车，所以记住上一条 URL
  // 去重（浏览器前进/后退是真实换页，URL 不同，会照常上报）。
  if (url === lastTrackedUrl) return

  lastTrackedUrl = url

  whenTitleUpdated(document.title).then(() => {
    window.gtag?.('event', 'page_view', {
      page_path: url,
      page_location: window.location.href,
      page_title: document.title,
    })
  })
})

// 注册 service worker：只挂 manifest 装不上，也收不到 Web Push。
// 放在 load 之后，别和首屏渲染抢资源。
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker').catch((error) => {
      // 注册失败不该影响应用可用性（http 环境、隐私模式下都可能失败）
      console.warn('[PWA] service worker 注册失败：', error)
    })
  })
}
